import { Fragment, useEffect, useId, useRef, useState } from "react";

import { useListboxNav } from "../interaction";
import { Popover, type Placement } from "./Popover";
import { cn } from "../lib/cn";

export interface DropdownItem {
  id: string;
  /** Must be a plain string when typeahead should find the item. */
  label: React.ReactNode;
  icon?: React.ReactNode;
  trailing?: React.ReactNode;
  tone?: "default" | "danger";
  disabled?: boolean;
  dividerAfter?: boolean;
  /** Extra attributes for the menuitem row (testids, RUM action names). */
  rowProps?: React.HTMLAttributes<HTMLDivElement> & {
    [attr: `data-${string}`]: string | undefined;
  };
}

export interface DropdownProps {
  items: DropdownItem[];
  onSelect: (id: string) => void;
  /** Spread the given wiring and ARIA props onto your own trigger. */
  trigger: (props: {
    ref: React.Ref<HTMLButtonElement>;
    onClick: () => void;
    onKeyDown: React.KeyboardEventHandler;
    "aria-haspopup": "menu";
    "aria-expanded": boolean;
    "aria-controls": string;
  }) => React.ReactNode;
  placement?: Placement;
  className?: string;
  /** Above the items, outside keyboard navigation; not a menuitem. */
  header?: React.ReactNode;
}

function itemText(item: DropdownItem | undefined): string {
  return typeof item?.label === "string" ? item.label : "";
}

const toneRest: Record<"default" | "danger", string> = {
  default: "text-ink-body",
  danger: "text-ink-stopped",
};

const toneActive: Record<"default" | "danger", string> = {
  default: "bg-recess text-ink",
  danger: "bg-wash-stopped/30 text-ink-stopped",
};

/** WAI-ARIA action menu with the highlight tracked by `aria-activedescendant`. */
export function Dropdown({
  items,
  onSelect,
  trigger,
  placement = "bottom-end",
  className,
  header,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const itemId = (index: number) => `${menuId}-item-${index}`;

  const firstEnabled = () => items.findIndex((item) => !item.disabled);
  const lastEnabled = () => {
    for (let index = items.length - 1; index >= 0; index -= 1) {
      if (!items[index].disabled) return index;
    }
    return -1;
  };

  const nav = useListboxNav({
    itemCount: items.length,
    getLabel: (index) => itemText(items[index]),
    isDisabled: (index) => Boolean(items[index]?.disabled),
    onSelect: (index) => selectAt(index),
    onDismiss: () => closeMenu(),
    onTabOut: () => closeMenu(),
  });

  const openMenu = (edge: "first" | "last") => {
    setOpen(true);
    nav.setActiveIndex(edge === "first" ? firstEnabled() : lastEnabled());
  };

  const closeMenu = () => {
    setOpen(false);
    nav.setActiveIndex(-1);
    triggerRef.current?.focus();
  };

  const selectAt = (index: number) => {
    const item = items[index];
    if (!item || item.disabled) return;
    closeMenu();
    onSelect(item.id);
  };

  // Light dismiss closes the menu before the trigger's click, so record open state at pointerdown.
  const openAtPressRef = useRef<boolean | null>(null);
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  });
  useEffect(() => {
    const element = triggerRef.current;
    if (!element) return;
    const handlePointerDown = () => {
      openAtPressRef.current = openRef.current;
    };
    element.addEventListener("pointerdown", handlePointerDown);
    return () => element.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  const handleTriggerClick = () => {
    const wasOpen = openAtPressRef.current ?? open;
    openAtPressRef.current = null;
    if (wasOpen) {
      setOpen(false);
      nav.setActiveIndex(-1);
      return;
    }
    openMenu("first");
  };

  const handleTriggerKeyDown: React.KeyboardEventHandler = (event) => {
    openAtPressRef.current = null;
    if (open) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openMenu("first");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openMenu("last");
    }
  };

  // The child Popover's effect (showing it) runs first, so the menu is focusable here.
  useEffect(() => {
    if (open) menuRef.current?.focus();
  }, [open]);

  return (
    <>
      {trigger({
        ref: triggerRef,
        onClick: handleTriggerClick,
        onKeyDown: handleTriggerKeyDown,
        "aria-haspopup": "menu",
        "aria-expanded": open,
        "aria-controls": menuId,
      })}
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) nav.setActiveIndex(-1);
        }}
        anchorRef={triggerRef}
        placement={placement}
        id={menuId}
        role="menu"
        ref={menuRef}
        tabIndex={-1}
        aria-activedescendant={
          nav.activeIndex >= 0 ? itemId(nav.activeIndex) : undefined
        }
        onKeyDown={nav.handleKeyDown}
        className={cn("min-w-40 focus-visible:outline-none", className)}
      >
        {header && (
          <div
            role="presentation"
            className="mb-1 border-b border-rule px-3 pt-2 pb-2.5"
          >
            {header}
          </div>
        )}
        {items.map((item, index) => {
          const tone = item.tone ?? "default";
          const active = index === nav.activeIndex && !item.disabled;
          return (
            // menuitem and separator must be direct children of the menu for AT.
            <Fragment key={item.id}>
              <div
                {...item.rowProps}
                id={itemId(index)}
                role="menuitem"
                aria-disabled={item.disabled || undefined}
                onPointerEnter={() => {
                  if (!item.disabled) nav.setActiveIndex(index);
                }}
                onClick={() => selectAt(index)}
                className={cn(
                  "flex cursor-default items-center gap-2 rounded-[2px] px-3 py-2 text-sm select-none",
                  "transition-colors duration-150 ease-paper motion-reduce:transition-none",
                  item.disabled
                    ? "cursor-not-allowed text-faint"
                    : cn(toneRest[tone], active && toneActive[tone]),
                )}
              >
                {item.icon && (
                  <span
                    aria-hidden="true"
                    className="grid size-4 shrink-0 place-items-center"
                  >
                    {item.icon}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {item.trailing}
              </div>
              {item.dividerAfter && (
                <div role="separator" className="my-1 border-t border-rule" />
              )}
            </Fragment>
          );
        })}
      </Popover>
    </>
  );
}
